import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiBarChart3, 
  FiCalendar, FiSettings, FiTarget, FiMessageSquare, FiSave,
  FiX, FiPhone, FiClock, FiUsers, FiTrendingUp
} from 'react-icons/fi';
import adminApi from '../../utils/adminApi';

const AdminCampaigns = () => {
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    subtitle: '',
    description: '',
    primary_button_text: 'Book Free Consultation',
    primary_button_link: '/contact',
    show_secondary_button: true,
    secondary_button_text: 'WhatsApp Us',
    whatsapp_number: '+919355353459',
    whatsapp_message: "Hi! I'd like to know more about your consultation services.",
    is_active: true,
    delay_seconds: 3,
    position: 'center',
    max_displays_per_user: 1,
    cooldown_hours: 24,
    features: ['Personalized nutrition plan', 'Expert dietitian consultation', 'Goal-specific recommendations'],
    dismissal_text: "This popup won't show again for 24 hours",
    theme_color: '#f59e0b',
    show_on_pages: [],
    exclude_pages: []
  });

  useEffect(() => {
    fetchBanners();
    fetchStats();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await adminApi.get('/banners');
      setBanners(response.data.banners || []);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/banners/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingBanner(null);
    setFormData({
      name: '',
      title: '',
      subtitle: '',
      description: '',
      primary_button_text: 'Book Free Consultation',
      primary_button_link: '/contact',
      show_secondary_button: true,
      secondary_button_text: 'WhatsApp Us',
      whatsapp_number: '+919355353459',
      whatsapp_message: "Hi! I'd like to know more about your consultation services.",
      is_active: true,
      delay_seconds: 3,
      position: 'center',
      max_displays_per_user: 1,
      cooldown_hours: 24,
      features: ['Personalized nutrition plan', 'Expert dietitian consultation', 'Goal-specific recommendations'],
      dismissal_text: "This popup won't show again for 24 hours",
      theme_color: '#f59e0b',
      show_on_pages: [],
      exclude_pages: []
    });
    setShowForm(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      ...banner,
      features: banner.features || ['Personalized nutrition plan', 'Expert dietitian consultation', 'Goal-specific recommendations'],
      show_on_pages: banner.show_on_pages || [],
      exclude_pages: banner.exclude_pages || []
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingBanner) {
        await adminApi.put(`/banners/${editingBanner.id}`, formData);
      } else {
        await adminApi.post('/banners', formData);
      }
      
      setShowForm(false);
      fetchBanners();
      fetchStats();
    } catch (error) {
      console.error('Failed to save banner:', error);
    }
  };

  const handleDelete = async (bannerId) => {
    if (window.confirm('Are you sure you want to delete this campaign banner?')) {
      try {
        await adminApi.delete(`/banners/${bannerId}`);
        fetchBanners();
        fetchStats();
      } catch (error) {
        console.error('Failed to delete banner:', error);
      }
    }
  };

  const handleToggleStatus = async (bannerId) => {
    try {
      await adminApi.post(`/banners/${bannerId}/toggle`);
      fetchBanners();
      fetchStats();
    } catch (error) {
      console.error('Failed to toggle banner status:', error);
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ 
      ...formData, 
      features: [...formData.features, ''] 
    });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">Campaign Banners</h1>
            <p className="text-gray-600 mt-1">Manage consultation booking popups</p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <FiPlus className="w-5 h-5" />
            <span>Create Campaign</span>
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Banners</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_banners}</p>
                </div>
                <FiTarget className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_banners}</p>
                </div>
                <FiEye className="w-8 h-8 text-green-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total_views.toLocaleString()}</p>
                </div>
                <FiUsers className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.overall_conversion_rate}%</p>
                </div>
                <FiTrendingUp className="w-8 h-8 text-orange-500" />
              </div>
            </div>
          </div>
        )}

        {/* Banners List */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Campaign Banners</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {banners.map((banner) => (
                  <tr key={banner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{banner.name}</div>
                        <div className="text-sm text-gray-500">{banner.title}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        banner.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {banner.clicks_count} clicks / {banner.views_count} views
                      </div>
                      <div className="text-sm text-gray-500">
                        {banner.conversion_rate.toFixed(1)}% conversion
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(banner.start_date).toLocaleDateString()}
                      {banner.end_date && (
                        <div>to {new Date(banner.end_date).toLocaleDateString()}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(banner)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(banner.id)}
                          className={banner.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                        >
                          {banner.is_active ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(banner.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingBanner ? 'Edit Campaign Banner' : 'Create Campaign Banner'}
                  </h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Name (Internal)
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field"
                      placeholder="e.g., Holiday Consultation Campaign"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Popup Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="input-field"
                      placeholder="Book Your Free Consultation!"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="input-field"
                    placeholder="Start your transformation journey today"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field h-24"
                    placeholder="Get personalized nutrition advice from our expert dietitian..."
                  />
                </div>

                {/* Features */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Features/Benefits
                  </label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="input-field flex-1"
                        placeholder="Feature description"
                      />
                      <button
                        onClick={() => removeFeature(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addFeature}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                  >
                    <FiPlus className="w-4 h-4" />
                    <span>Add Feature</span>
                  </button>
                </div>

                {/* Button Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Button Text
                    </label>
                    <input
                      type="text"
                      value={formData.primary_button_text}
                      onChange={(e) => setFormData({ ...formData, primary_button_text: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Button Link
                    </label>
                    <input
                      type="text"
                      value={formData.primary_button_link}
                      onChange={(e) => setFormData({ ...formData, primary_button_link: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Secondary Button */}
                <div>
                  <label className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      checked={formData.show_secondary_button}
                      onChange={(e) => setFormData({ ...formData, show_secondary_button: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Show WhatsApp Button</span>
                  </label>
                  
                  {formData.show_secondary_button && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={formData.secondary_button_text}
                        onChange={(e) => setFormData({ ...formData, secondary_button_text: e.target.value })}
                        className="input-field"
                        placeholder="WhatsApp Us"
                      />
                      <input
                        type="text"
                        value={formData.whatsapp_number}
                        onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                        className="input-field"
                        placeholder="+919355353459"
                      />
                      <div className="md:col-span-2">
                        <textarea
                          value={formData.whatsapp_message}
                          onChange={(e) => setFormData({ ...formData, whatsapp_message: e.target.value })}
                          className="input-field h-20"
                          placeholder="WhatsApp message..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Display Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delay (seconds)
                    </label>
                    <input
                      type="number"
                      value={formData.delay_seconds}
                      onChange={(e) => setFormData({ ...formData, delay_seconds: parseInt(e.target.value) })}
                      className="input-field"
                      min="0"
                      max="60"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position
                    </label>
                    <select
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="input-field"
                    >
                      <option value="center">Center</option>
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cooldown (hours)
                    </label>
                    <input
                      type="number"
                      value={formData.cooldown_hours}
                      onChange={(e) => setFormData({ ...formData, cooldown_hours: parseInt(e.target.value) })}
                      className="input-field"
                      min="1"
                      max="168"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Active Campaign</span>
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary flex items-center space-x-2"
                >
                  <FiSave className="w-4 h-4" />
                  <span>{editingBanner ? 'Update' : 'Create'} Campaign</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCampaigns;